import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subscription } from 'rxjs';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { CompraService } from '@shared/services/apis/compras/compra.service';
import { CompraSplitLadoStruct } from '@shared/services/structs/compras/compra-split-lado.struct';
import { formatCodigoCompra } from '../card-edit-dialog.types';

/**
 * Painel "Split de pedido" do card. Busca o split da compra e só aparece quando ela é filha
 * (mostra o pai) ou pai (lista as filhas). A reversão é sempre de uma filha.
 */
@Component({
  selector: 'app-ced-split',
  standalone: true,
  imports: [
    DatePipe,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
  templateUrl: './ced-split.component.html',
  styleUrls: ['./ced-split.component.css'],
})
export class CedSplitComponent implements OnInit, OnDestroy {
  @Input({ required: true }) idCompra!: number;
  /** Código de exibição da compra aberta ("#1030"). */
  @Input() codigo = '';
  /** Etapa atual da compra aberta: enquanto a reversão está liberada, é a mesma etapa do split. */
  @Input() nomeEtapaAtual = '';
  @Input() somenteLeitura = false;

  /** Split revertido: o shell recarrega o card e o quadro. */
  @Output() revertido = new EventEmitter<void>();

  pai: CompraSplitLadoStruct | null = null;
  filhos: CompraSplitLadoStruct[] = [];
  /** Lado cujo split está sendo revertido (botão em carregamento); com ele definido, todos os botões ficam travados. */
  revertendoLado: CompraSplitLadoStruct | null = null;
  private readonly subs = new Subscription();

  constructor(
    private compraService: CompraService,
    private alertService: AlertService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.recarregar();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  recarregar(): void {
    if (!this.idCompra) return;
    this.subs.add(this.compraService.getSplit(this.idCompra).subscribe({
      next: (res) => {
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription || 'Não foi possível carregar o split do pedido.', AlertType.error);
          return;
        }
        this.pai = res.pai ?? null;
        this.filhos = res.filhos ?? [];
      },
      error: () => this.alertService.show('Erro', 'Não foi possível carregar o split do pedido.', AlertType.error),
    }));
  }

  codigoCompra(codigo: string): string {
    return formatCodigoCompra(codigo);
  }

  /** Motivo do "Reverter split" desabilitado (vazio = liberado). */
  motivoBloqueio(lado: CompraSplitLadoStruct): string {
    if (!lado.podeReverter) return lado.motivoBloqueio || 'O split não pode mais ser revertido.';
    if (this.somenteLeitura) return 'Pedido arquivado ou em etapa final: o split não pode ser revertido.';
    return '';
  }

  /**
   * Reverte o split de uma filha. `lado` é o pai (compra aberta é a filha) ou uma das filhas
   * (compra aberta é o pai).
   */
  reverter(lado: CompraSplitLadoStruct): void {
    if (this.revertendoLado) return;
    const isFilhaAberta = lado === this.pai;
    const idCompraFilho = isFilhaAberta ? this.idCompra : lado.idCompra;
    const codigoPai = isFilhaAberta ? this.codigoCompra(lado.codigo) : this.codigo;
    this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Reverter split',
        description: isFilhaAberta
          ? `O saldo volta para o pedido ${codigoPai} e este pedido será arquivado.`
          : `O saldo volta para este pedido e o pedido ${this.codigoCompra(lado.codigo)} será arquivado.`,
        buttonConfirm: 'Reverter',
        buttonClose: 'Cancelar',
      },
    }).afterClosed().subscribe((result) => {
      if (!result?.confirm) return;
      this.revertendoLado = lado;
      this.subs.add(this.compraService.reverterSplit(idCompraFilho).subscribe({
        next: (res) => {
          this.revertendoLado = null;
          if (res.isError) {
            this.alertService.show('Erro', res.errorDescription || 'Não foi possível reverter o split.', AlertType.error);
            return;
          }
          this.alertService.show('Sucesso', `Split revertido: o saldo voltou para o pedido ${codigoPai}.`, AlertType.success);
          this.revertido.emit();
        },
        error: () => {
          this.revertendoLado = null;
          this.alertService.show('Erro', 'Não foi possível reverter o split.', AlertType.error);
        },
      }));
    });
  }
}
